// Custom hook for easier permission checking in components

import { useAuth } from '../contexts/AuthContext';
import { 
  Permission, 
  UserRole, 
  ResourceContext,
  ArticleAction,
  MediaAction,
  UserAction
} from '../types/permissions';

export const usePermissions = () => {
  const { 
    userData, 
    checkPermission, 
    canPerformAction, 
    hasRole, 
    hasMinimumRole,
    canAccessAdmin,
    canAccessEditor
  } = useAuth();

  /**
   * Check if current user has a specific permission
   */
  const hasPermission = (permission: Permission, resource?: ResourceContext): boolean => {
    return checkPermission(permission, resource);
  };

  /**
   * Check if current user can perform an action
   */
  const canPerform = (
    action: ArticleAction | MediaAction | UserAction, 
    scope: 'own' | 'all' = 'own'
  ): boolean => {
    return canPerformAction(action, scope);
  };

  /**
   * Check if editor can act on article based on assigned tags
   */
  const canActOnArticleByTags = (articleTags?: string[]): boolean => {
    // Admins and owners can act on any article
    if (userData?.role === 'admin' || userData?.role === 'owner') {
      return true;
    }

    // Writers can only act on their own articles (checked elsewhere)
    if (userData?.role === 'writer') {
      return true;
    }

    // Editors need matching tags
    if (userData?.role === 'editor') {
      if (!userData.assignedTags || userData.assignedTags.length === 0) {
        return false;
      }

      if (!articleTags || articleTags.length === 0) {
        return false;
      }

      // Check if any article tag matches editor's assigned tags
      // Note: articleTags are IDs, assignedTags are slugs - need tag resolution in the component
      return true; // Component-level check needed
    }

    return false;
  };

  /**
   * Article-specific permission checks
   */
  const articlePermissions = {
    canCreate: () => hasPermission(Permission.ARTICLE_CREATE),
    canReadOwn: () => hasPermission(Permission.ARTICLE_READ_OWN),
    canReadAll: () => hasPermission(Permission.ARTICLE_READ_ALL),
    canEditOwn: (authorId?: string) => 
      hasPermission(Permission.ARTICLE_EDIT_OWN, { authorId }),
    canEditAll: () => hasPermission(Permission.ARTICLE_EDIT_ALL),
    canDeleteOwn: (authorId?: string) => 
      hasPermission(Permission.ARTICLE_DELETE_OWN, { authorId }),
    canDeleteAll: () => hasPermission(Permission.ARTICLE_DELETE_ALL),
    canPublishOwn: (authorId?: string) => 
      hasPermission(Permission.ARTICLE_PUBLISH_OWN, { authorId }),
    canPublishAll: () => hasPermission(Permission.ARTICLE_PUBLISH_ALL),
    canAssign: () => hasPermission(Permission.ARTICLE_ASSIGN),
    
    // Tag-based permission checks for editors
    canEditArticleByTag: (articleTags?: string[]) => {
      return articlePermissions.canEditAll() && canActOnArticleByTags(articleTags);
    },
    canPublishArticleByTag: (articleTags?: string[]) => {
      return articlePermissions.canPublishAll() && canActOnArticleByTags(articleTags);
    },
    canDeleteArticleByTag: (articleTags?: string[]) => {
      return articlePermissions.canDeleteAll() && canActOnArticleByTags(articleTags);
    },
    
    // Convenience methods
    canEdit: (authorId?: string) => {
      const isOwn = authorId === userData?.id;
      return isOwn 
        ? articlePermissions.canEditOwn(authorId)
        : articlePermissions.canEditAll();
    },
    canDelete: (authorId?: string) => {
      const isOwn = authorId === userData?.id;
      return isOwn 
        ? articlePermissions.canDeleteOwn(authorId)
        : articlePermissions.canDeleteAll();
    },
    canPublish: (authorId?: string) => {
      const isOwn = authorId === userData?.id;
      return isOwn 
        ? articlePermissions.canPublishOwn(authorId)
        : articlePermissions.canPublishAll();
    }
  };

  /**
   * Media library permission checks
   */
  const mediaPermissions = {
    canUpload: () => hasPermission(Permission.MEDIA_UPLOAD),
    canReadOwn: () => hasPermission(Permission.MEDIA_READ_OWN),
    canReadAll: () => hasPermission(Permission.MEDIA_READ_ALL),
    canEditOwn: (uploadedBy?: string) => 
      hasPermission(Permission.MEDIA_EDIT_OWN, { uploadedBy }),
    canEditAll: () => hasPermission(Permission.MEDIA_EDIT_ALL),
    canDeleteOwn: (uploadedBy?: string) => 
      hasPermission(Permission.MEDIA_DELETE_OWN, { uploadedBy }),
    canDeleteAll: () => hasPermission(Permission.MEDIA_DELETE_ALL),
    
    // Folder permissions
    canCreateFolders: () => hasPermission(Permission.MEDIA_FOLDER_CREATE),
    canEditFolders: () => hasPermission(Permission.MEDIA_FOLDER_EDIT),
    canDeleteFolders: () => hasPermission(Permission.MEDIA_FOLDER_DELETE),
    canMoveItems: () => hasPermission(Permission.MEDIA_FOLDER_MOVE_ITEMS),
    
    // Convenience methods
    canEdit: (uploadedBy?: string) => {
      const isOwn = uploadedBy === userData?.id;
      return isOwn 
        ? mediaPermissions.canEditOwn(uploadedBy)
        : mediaPermissions.canEditAll();
    },
    canDelete: (uploadedBy?: string) => {
      const isOwn = uploadedBy === userData?.id;
      return isOwn 
        ? mediaPermissions.canDeleteOwn(uploadedBy)
        : mediaPermissions.canDeleteAll();
    }
  };

  /**
   * User management permission checks
   */
  const userPermissions = {
    canCreate: () => hasPermission(Permission.USER_CREATE),
    canReadAll: () => hasPermission(Permission.USER_READ_ALL),
    canReadProfiles: () => hasPermission(Permission.USER_READ_PROFILES),
    canEditOwn: () => hasPermission(Permission.USER_EDIT_OWN),
    canEditAll: () => hasPermission(Permission.USER_EDIT_ALL),
    canDelete: () => hasPermission(Permission.USER_DELETE),
    canChangeRoles: () => hasPermission(Permission.USER_CHANGE_ROLES),
    canActivateDeactivate: () => hasPermission(Permission.USER_ACTIVATE_DEACTIVATE),
  };

  /**
   * Category management permission checks
   */
  const categoryPermissions = {
    canRead: () => hasPermission(Permission.CATEGORY_READ),
    canCreate: () => hasPermission(Permission.CATEGORY_CREATE),
    canEdit: () => hasPermission(Permission.CATEGORY_EDIT),
    canDelete: () => hasPermission(Permission.CATEGORY_DELETE),
  };

  /**
   * Ad management permission checks
   */
  const adPermissions = {
    canManage: () => hasPermission(Permission.ADS_MANAGE),
  };

  /**
   * Dashboard and UI permission checks
   */
  const uiPermissions = {
    canAccessDashboard: () => hasPermission(Permission.DASHBOARD_ACCESS),
    canAccessAdmin: canAccessAdmin,
    canAccessEditor: canAccessEditor,
    canViewAnalytics: () => hasPermission(Permission.ANALYTICS_VIEW),
    canViewSiteConfig: () => hasPermission(Permission.SITE_CONFIG_VIEW),
    canEditSiteConfig: () => hasPermission(Permission.SITE_CONFIG_EDIT),
    canModerateComments: () => hasPermission(Permission.COMMENT_MODERATE),
  };

  /**
   * Role checks
   */
  const roleChecks = {
    isWriter: () => hasRole('writer'),
    isEditor: () => hasRole('editor'),
    isAdmin: () => hasRole('admin'),
    isOwner: () => hasRole('owner'),
    hasMinimum: (role: UserRole) => hasMinimumRole(role),
  };

  /**
   * Get available article statuses for current user
   */
  const getAvailableArticleStatuses = (): ('draft' | 'published')[] => {
    const statuses: ('draft' | 'published')[] = ['draft'];
    
    // Only editors and admins can publish articles
    if (articlePermissions.canPublishAll()) {
      statuses.push('published');
    }
    
    return statuses;
  };

  /**
   * Check if user can perform bulk operations
   */
  const canPerformBulkOperations = (): boolean => {
    return roleChecks.isEditor() || roleChecks.isAdmin();
  };

  /**
   * Get user's role-based menu items
   */
  const getMenuItems = () => {
    const menuItems = [];

    // Basic dashboard access
    if (uiPermissions.canAccessDashboard()) {
      menuItems.push('dashboard');
    }

    // Article management
    if (articlePermissions.canCreate() || articlePermissions.canReadOwn()) {
      menuItems.push('articles');
    }

    // Media library
    if (mediaPermissions.canUpload() || mediaPermissions.canReadOwn()) {
      menuItems.push('media');
    }

    // User management (editors and admins)
    if (userPermissions.canReadProfiles()) {
      menuItems.push('users');
    }

    // Category management (admins only)
    if (categoryPermissions.canCreate()) {
      menuItems.push('categories');
    }

    // Analytics (editors and admins)
    if (uiPermissions.canViewAnalytics()) {
      menuItems.push('analytics');
    }

    // Site configuration (admins only)
    if (uiPermissions.canViewSiteConfig()) {
      menuItems.push('settings');
    }

    return menuItems;
  };

  return {
    // Core permission checking
    hasPermission,
    canPerform,
    
    // Specific permission groups
    article: articlePermissions,
    media: mediaPermissions,
    user: userPermissions,
    category: categoryPermissions,
    ad: adPermissions,
    ui: uiPermissions,
    role: roleChecks,
    
    // Utility functions
    getAvailableArticleStatuses,
    canPerformBulkOperations,
    getMenuItems,
    
    // User data
    currentUser: userData,
    isAuthenticated: !!userData,
    isActive: userData?.isActive || false,
  };
};

